import { z } from 'zod';
import {
  ConsumerCommissionStatus,
  ConsumerCommissionStatusSchema,
  ConsumerStatus,
  ConsumerStatusSchema,
  ConsumerType,
  ConsumerTypeSchema,
  DEFAULT_CONSUMER_TYPE,
  ReferralStatus,
  ReferralStatusSchema,
} from '../enums/consumer.enums';

// ============================================================================
// RE-EXPORTS FOR BACKWARDS COMPATIBILITY
// ============================================================================

// Re-export enums and schemas
export {
  ConsumerCommissionStatus,
  ConsumerCommissionStatusSchema,
  ConsumerStatus,
  ConsumerStatusSchema,
  ConsumerType,
  ConsumerTypeSchema,
  DEFAULT_CONSUMER_TYPE,
  ReferralStatus,
  ReferralStatusSchema,
};

// ============================================================================
// CONSUMER DTOs
// ============================================================================

/**
 * Consumer DTO Schema
 * Represents a B2B2C consumer (end-user of a business business)
 */
export const ConsumerSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  phone: z.string().nullable().optional(),
  status: ConsumerStatusSchema,
  consumerType: ConsumerTypeSchema.default(ConsumerType.END_CONSUMER),
  emailVerified: z.boolean(),
  profile: z.record(z.string(), z.unknown()).nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  lastLoginAt: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Consumer = z.infer<typeof ConsumerSchema>;

/**
 * Create Consumer DTO Schema
 * Used by business admins to create consumers
 */
export const CreateConsumerSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(255),
  phone: z.string().optional(),
  status: ConsumerStatusSchema.optional(),
  consumerType: ConsumerTypeSchema.optional(),
  profile: z.record(z.string(), z.unknown()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateConsumer = z.infer<typeof CreateConsumerSchema>;

/**
 * Update Consumer DTO Schema
 */
export const UpdateConsumerSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    phone: z.string().optional(),
    status: ConsumerStatusSchema.optional(),
    consumerType: ConsumerTypeSchema.optional(),
    profile: z.record(z.string(), z.unknown()).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });
export type UpdateConsumer = z.infer<typeof UpdateConsumerSchema>;

/**
 * Consumer Response DTO (excludes sensitive auth data)
 */
export const ConsumerResponseSchema = ConsumerSchema;
export type ConsumerResponse = z.infer<typeof ConsumerResponseSchema>;

// ============================================================================
// CONSUMER AUTH DTOs
// ============================================================================

/**
 * Consumer Registration Schema
 * Self-registration by consumers
 */
export const ConsumerRegisterSchema = z.object({
  businessId: z.string().uuid(), // Which business they're registering with
  email: z.string().email(),
  name: z.string().min(1).max(255),
  phone: z.string().optional(),
  password: z.string().min(8).max(128).optional(), // Optional - can use magic link instead
});
export type ConsumerRegister = z.infer<typeof ConsumerRegisterSchema>;

/**
 * Consumer Login Schema
 */
export const ConsumerLoginSchema = z.object({
  businessId: z.string().uuid(),
  email: z.string().email(),
  password: z.string().min(1).max(128),
});
export type ConsumerLogin = z.infer<typeof ConsumerLoginSchema>;

/**
 * Magic Link Send Schema
 */
export const MagicLinkSendSchema = z.object({
  businessId: z.string().uuid(),
  email: z.string().email(),
});
export type MagicLinkSend = z.infer<typeof MagicLinkSendSchema>;

/**
 * Magic Link Verify Schema
 */
export const MagicLinkVerifySchema = z.object({
  token: z.string().min(1),
});
export type MagicLinkVerify = z.infer<typeof MagicLinkVerifySchema>;

/**
 * Consumer Auth Response Schema
 */
export const ConsumerAuthResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  consumer: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    businessId: z.string().uuid(),
    consumerType: ConsumerTypeSchema,
  }),
});
export type ConsumerAuthResponse = z.infer<typeof ConsumerAuthResponseSchema>;

/**
 * Consumer Token Refresh Schema
 */
export const ConsumerRefreshTokenSchema = z.object({
  refreshToken: z.string().min(1),
});
export type ConsumerRefreshToken = z.infer<typeof ConsumerRefreshTokenSchema>;

// ============================================================================
// CONSUMER PROFILE DTOs
// ============================================================================

/**
 * Update Consumer Profile Schema
 * Used by consumers to update their own profile
 */
export const UpdateConsumerProfileSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    phone: z.string().optional(),
    profile: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });
export type UpdateConsumerProfile = z.infer<typeof UpdateConsumerProfileSchema>;

// ============================================================================
// CONSUMER LIST DTOs
// ============================================================================

/**
 * Consumer List Query Schema
 */
export const ConsumerListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  search: z.string().optional(),
  status: ConsumerStatusSchema.optional(),
  sortBy: z.enum(['name', 'email', 'createdAt', 'lastLoginAt']).optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type ConsumerListQuery = z.infer<typeof ConsumerListQuerySchema>;

/**
 * Consumer List Response Schema
 */
export const ConsumerListResponseSchema = z.object({
  data: z.array(ConsumerSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type ConsumerListResponse = z.infer<typeof ConsumerListResponseSchema>;

// ============================================================================
// CONSUMER PORTAL - COMMISSION DTOs (Issue #91)
// ============================================================================

export const CommissionSchema = z.object({
  id: z.string().uuid(),
  caseName: z.string(),
  amount: z.number(),
  rate: z.number(),
  status: ConsumerCommissionStatusSchema,
  paidAt: z.string().datetime().nullable(),
  description: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type Commission = z.infer<typeof CommissionSchema>;

export const CommissionListQuerySchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  status: ConsumerCommissionStatusSchema.optional(),
});
export type CommissionListQuery = z.infer<typeof CommissionListQuerySchema>;

export const CommissionListResponseSchema = z.object({
  data: z.array(CommissionSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type CommissionListResponse = z.infer<typeof CommissionListResponseSchema>;

export const CommissionStatementSchema = z.object({
  statementId: z.string(),
  period: z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
  }),
  partner: z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
  }),
  commissions: z.array(CommissionSchema),
  summary: z.object({
    totalCases: z.number(),
    totalVolume: z.number(),
    totalCommission: z.number(),
    pendingCommission: z.number(),
    paidCommission: z.number(),
  }),
});
export type CommissionStatement = z.infer<typeof CommissionStatementSchema>;

// ============================================================================
// CONSUMER PORTAL - REFERRAL DTOs (Issue #93)
// ============================================================================

export const ReferralSchema = z.object({
  id: z.string().uuid(),
  clientName: z.string(),
  clientEmail: z.string().email(),
  referralType: z.string(),
  status: ReferralStatusSchema,
  convertedToCase: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Referral = z.infer<typeof ReferralSchema>;

export const CreateReferralSchema = z.object({
  clientName: z.string().min(1),
  clientEmail: z.string().email(),
  referralType: z.string().min(1),
  notes: z.string().optional(),
});
export type CreateReferral = z.infer<typeof CreateReferralSchema>;

export const UpdateReferralSchema = z
  .object({
    clientName: z.string().optional(),
    clientEmail: z.string().email().optional(),
    referralType: z.string().optional(),
    status: ReferralStatusSchema.optional(),
    notes: z.string().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });
export type UpdateReferral = z.infer<typeof UpdateReferralSchema>;

export const ReferralListQuerySchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  status: ReferralStatusSchema.optional(),
  search: z.string().optional(),
});
export type ReferralListQuery = z.infer<typeof ReferralListQuerySchema>;

export const ReferralListResponseSchema = z.object({
  data: z.array(ReferralSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type ReferralListResponse = z.infer<typeof ReferralListResponseSchema>;
